import * as i0 from '@angular/core';
import { Pipe, Directive, Optional, Self, Input, input, output, signal, computed, Component, ViewChild, inject, Injectable } from '@angular/core';
import * as i1 from '@angular/forms';
import { NgControl, NG_VALIDATORS } from '@angular/forms';
import { NgClass, NgTemplateOutlet, NgFor, NgIf, NgForOf } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

class AbbreviatePipe {
    transform(text, abbrLength = 20) {
        if (text.length > abbrLength) {
            return text.substring(0, abbrLength) + '..';
        }
        return text;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AbbreviatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: AbbreviatePipe, isStandalone: true, name: "abbreviate" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AbbreviatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'abbreviate',
                    standalone: true
                }]
        }] });

function isString(thing) {
    return typeof thing === 'string';
}
function isFunction(thing) {
    return typeof thing === 'function';
}

class AccessibilityDirective {
    elementRef;
    ngControl;
    parentForm;
    parentFormGroup;
    static REQUIRED_CLASS = 'uss-angular-util-accessibility-required';
    isRequired = false;
    markedAsRequired = false;
    set required(val) {
        this.isRequired = val ? true : isString(val);
    }
    get required() {
        return this.isRequired;
    }
    constructor(elementRef, ngControl, parentForm, parentFormGroup) {
        this.elementRef = elementRef;
        this.ngControl = ngControl;
        this.parentForm = parentForm;
        this.parentFormGroup = parentFormGroup;
    }
    /** watch bound values */
    ngOnChanges() {
        if (this.markedAsRequired !== this.isRequired) {
            this.markLabel();
            this.markedAsRequired = this.required;
        }
    }
    markLabel() {
        const labels = this.findLabels();
        const addLabel = this.required && !this.elementRef.nativeElement.attributes['no-asterisk'];
        for (let i = 0, len = labels.length; i < len; i++) {
            const node = labels[i];
            if (addLabel) {
                node.classList.add(AccessibilityDirective.REQUIRED_CLASS);
            }
            else {
                node.classList.remove(AccessibilityDirective.REQUIRED_CLASS);
            }
        }
    }
    findLabels(elementRef = this.elementRef) {
        const labels = this.elementRef.nativeElement.labels || this.queryLabels(elementRef);
        return labels || {};
    }
    queryLabels(elementRef = this.elementRef) {
        const safeId = this.escapeRegExp(elementRef.nativeElement.id);
        return document && document.querySelectorAll && document.querySelectorAll(`[for=${safeId}]`);
    }
    /**
     * Copied from MDN. Prevents the string characters from being used as a regex.
     */
    escapeRegExp(stringToEscape) {
        return stringToEscape ? stringToEscape.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&') : stringToEscape; // $& means the whole matched string
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccessibilityDirective, deps: [{ token: i0.ElementRef }, { token: i1.NgControl, optional: true, self: true }, { token: i1.NgForm, optional: true }, { token: i1.FormGroupDirective, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: AccessibilityDirective, isStandalone: true, selector: "input, textarea, select", inputs: { required: "required" }, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AccessibilityDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: `input, textarea, select`,
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i1.NgForm, decorators: [{
                    type: Optional
                }] }, { type: i1.FormGroupDirective, decorators: [{
                    type: Optional
                }] }], propDecorators: { required: [{
                type: Input
            }] } });

class AmountInputValidator {
    constructor() { }
    validate(precision = null, modulus = null) {
        return (c) => {
            if (c && c.value) {
                const result = {};
                const state = {
                    val: this.resolveNumber(c),
                    precision: this.resolveNumber(precision),
                    modulus: this.resolveNumber(modulus),
                };
                const validPrecision = this.validatePrecision(state, result);
                const validModulus = this.validateModulus(state, result);
                return (validPrecision && validModulus) ? null : result;
            }
            return null;
        };
    }
    resolveNumber(val) {
        if (Number.isFinite(val) || val) {
            const value = val.value || val;
            return value ? +value : 0;
        }
        return null;
    }
    /**
     * @return true if there are no errors
     */
    validatePrecision(state, result) {
        const pattern = this.generatePattern(state.precision);
        const valStr = '' + state.val;
        if (Number.isFinite(state.precision) && !valStr.match(pattern)) {
            const actual = valStr.match(/\.(\d+)/);
            result['precision'] = {
                requiredPrecision: state.precision,
                actualPrecision: actual ? actual[0].length : 0,
                value: state.val,
            };
            return false;
        }
        else {
            return true;
        }
    }
    generatePattern(precision) {
        return precision ? new RegExp(`(^-?\\d*(\\.\\d{1,${precision}})?$)|(^-?\\d+\\.$)`) : /(^-?\d*\.?$)/;
    }
    /**
     * @return true if there are no errors
     */
    validateModulus(state, result) {
        if (Number.isFinite(state.modulus) && (state.val || 0) % (state.modulus || 0) !== 0) {
            result['modulus'] = {
                requiredModulus: state.modulus,
                value: state.val,
            };
            return false;
        }
        else {
            return true;
        }
    }
}

class AmountInputDirective {
    injector;
    amountInputValidator = new AmountInputValidator();
    precision;
    modulus;
    constructor(injector) {
        this.injector = injector;
    }
    ngOnChanges() {
        const ctrl = this.injector.get(NgControl);
        if (ctrl.control) {
            ctrl.control.updateValueAndValidity();
        }
    }
    validate(control) {
        return this.amountInputValidator.validate(this.precision, this.modulus)(control);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AmountInputDirective, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.1", type: AmountInputDirective, isStandalone: true, selector: "input[type=\"number\"][ngModel][ussAmountInput]", inputs: { precision: "precision", modulus: "modulus" }, providers: [{ provide: NG_VALIDATORS, useExisting: AmountInputDirective, multi: true }], usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AmountInputDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[type="number"][ngModel][ussAmountInput]',
                    providers: [{ provide: NG_VALIDATORS, useExisting: AmountInputDirective, multi: true }],
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.Injector }], propDecorators: { precision: [{
                type: Input
            }], modulus: [{
                type: Input
            }] } });

class BytePipe {
    radices = ['B', 'KB', 'MB', 'GB', 'TB'];
    regexp = new RegExp('([0-9]+)(\.[0-9])?.*');
    transform(bytes) {
        let radix = 0;
        bytes = +bytes;
        while (bytes >= 1000 && radix < this.radices.length - 1) {
            radix++;
            bytes /= 1000.0;
        }
        const bytesStr = '' + bytes;
        const matches = bytesStr.match(this.regexp);
        return (matches && matches[1] + (matches[2] || '') + this.radices[radix]) || '';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BytePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: BytePipe, isStandalone: true, name: "byte" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: BytePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'byte',
                    standalone: true
                }]
        }] });

class AlertComponent {
    alert;
    requestFocus = input(false);
    model = input.required();
    type = input('info');
    closeable = input(true);
    styling = input('d-flex align-items-center alert align-items-stretch py-3');
    infoStyling = input('alert-info    border-info');
    warningStyling = input('alert-warning border-warning');
    errorStyling = input('alert-danger  border-danger');
    fullWidth = input(false);
    closed = output();
    targetElement = signal(null);
    canFocus = computed(() => !!(this.targetElement() && isFunction(this.model().focus)));
    jumpToFieldName = computed(() => {
        const targetElement = this.targetElement();
        if (targetElement && targetElement.labels && targetElement.labels.length) {
            return targetElement.labels[0].textContent.replace(/[:?!\-.]/, '');
        }
    });
    typeStyling = computed(() => {
        switch (this.type()) {
            case 'info':
                return this.infoStyling();
            case 'warning':
                return this.warningStyling();
            case 'error':
                return this.errorStyling();
            default:
                return '';
        }
    });
    computedStyling = computed(() => {
        return [
            ...this.styling().split(' '),
            ...this.typeStyling().split(' '),
            ...(this.closeable() ? ['alert-dismissible'] : []),
        ];
    });
    ngOnInit() {
        this.resolveTargetElement();
    }
    ngAfterViewInit() {
        this.focusAlert();
    }
    focus() {
        this.handlePrefocus();
        this.handleFocus();
        return false;
    }
    // emit a close event but don't do anything else.
    // the code using this can remove... if needed.
    close() {
        this.closed.emit(this.model());
    }
    focusAlert() {
        if (this.requestFocus() && this.alert && this.alert.nativeElement) {
            this.alert.nativeElement.focus();
        }
    }
    resolveTargetElement() {
        const model = this.model();
        if (model && model.focus && isString(model.focus)) {
            this.targetElement.set(this.findElement(model.focus));
        }
    }
    handlePrefocus() {
        const preFocus = this.model().preFocus;
        if (preFocus) {
            preFocus.call(null, undefined);
        }
    }
    handleFocus() {
        const model = this.model();
        if (model && model.focus) {
            const focus = model.focus;
            if (isFunction(focus)) {
                focus.call(null);
            }
            else if (this.targetElement) {
                // allow the dom to be updated.
                setTimeout(() => {
                    this.targetElement().focus();
                }, 0);
            }
        }
    }
    findElement(selector) {
        return this.alert.nativeElement.ownerDocument.querySelector(`#${selector}, [name="${selector}"], [formControlName="${selector}"]`);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.1", type: AlertComponent, isStandalone: true, selector: "uss-alert", inputs: { requestFocus: { classPropertyName: "requestFocus", publicName: "requestFocus", isSignal: true, isRequired: false, transformFunction: null }, model: { classPropertyName: "model", publicName: "model", isSignal: true, isRequired: true, transformFunction: null }, type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null }, closeable: { classPropertyName: "closeable", publicName: "closeable", isSignal: true, isRequired: false, transformFunction: null }, styling: { classPropertyName: "styling", publicName: "styling", isSignal: true, isRequired: false, transformFunction: null }, infoStyling: { classPropertyName: "infoStyling", publicName: "infoStyling", isSignal: true, isRequired: false, transformFunction: null }, warningStyling: { classPropertyName: "warningStyling", publicName: "warningStyling", isSignal: true, isRequired: false, transformFunction: null }, errorStyling: { classPropertyName: "errorStyling", publicName: "errorStyling", isSignal: true, isRequired: false, transformFunction: null }, fullWidth: { classPropertyName: "fullWidth", publicName: "fullWidth", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { closed: "closed" }, viewQueries: [{ propertyName: "alert", first: true, predicate: ["alert"], descendants: true, static: true }], ngImport: i0, template: "<section #alert\n         [ngClass]=\"computedStyling()\"\n         role=\"alert\"\n         tabindex=\"-1\">\n\n  <div class=\"visually-hidden sr-only\">\n    <button (click)=\"focus()\">\n      <span>{{model().body}}</span>\n      <span>\n      @if (canFocus()) {\n        @if (model().alternateFocusDescription) {\n          {{model().alternateFocusDescription}}\n        }\n        @else {\n          Jump to\n          {{jumpToFieldName()\n          ? \"the '\" + jumpToFieldName() + \"' field\"\n          : 'the field that produced this message'}}.\n        }\n      }\n      </span>\n    </button>\n  </div>\n\n  <div class=\"col d-flex\"\n       [ngClass]=\"{'container': fullWidth()}\">\n    <!-- Icon -->\n    <div class=\"d-none d-md-flex me-3\" style=\"font-size: 4rem !important;\">\n      <ng-container [ngTemplateOutlet]=\"icon\" [ngTemplateOutletContext]=\"{type:type()}\" />\n    </div>\n\n    <!-- Content -->\n    <div class=\"d-flex flex-column justify-content-center\">\n\n      <!-- Heading -->\n      <div class=\"d-flex align-items-center mb-2\">\n\n        <!-- Mobile Icon\n             The icon shifts to here to become a part of the heading on mobile. -->\n        <div class=\"d-inline-flex d-md-none ms-0 me-2 fs-4\">\n          <ng-container [ngTemplateOutlet]=\"icon\" [ngTemplateOutletContext]=\"{type:type()}\" />\n        </div>\n\n        <h4 class=\"alert-heading fw-bold mb-0\">\n          {{model().title}}\n        </h4>\n\n      </div>\n\n      <!-- Body -->\n      <p class=\"mb-0\">{{model().body}}</p>\n\n      <!-- Focus Link -->\n      @if (canFocus()) {\n        <p class=\"mt-3 mb-0\">\n          <a href=\"\" class=\"alert-link\" (click)=\"focus()\">\n            @if (model().alternateFocusDescription) {\n              {{model().alternateFocusDescription}}\n            }\n            @else {\n              Jump to\n              {{jumpToFieldName()\n              ? 'the ' + jumpToFieldName() + ' field'\n              : 'the field that produced this message'}}.\n            }\n          </a>\n        </p>\n      }\n\n    </div>\n  </div>\n\n  <!-- Close Button -->\n  @if (closeable()) {\n    <div class=\"d-flex flex-shrink-0 justify-content-end text-end\">\n      <button type=\"button\"\n              class=\"close btn-close pt-3\"\n              (click)=\"close()\"\n              aria-label=\"Close this message\">\n      </button>\n    </div>\n  }\n\n</section>\n\n<ng-template #icon let-type=\"type\">\n  @switch (type) {\n    <!-- (i) -->\n    @case ('info')    {\n      <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"1em\" height=\"1em\" fill=\"currentColor\" class=\"bi bi-info-circle-fill my-1 mx-0\" viewBox=\"0 0 16 16\">\n        <path d=\"M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z\"/>\n      </svg>\n    }\n    <!-- (!) -->\n    @case ('warning') {\n      <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"1em\" height=\"1em\" fill=\"currentColor\" class=\"bi bi-exclamation-triangle-fill my-1 mx-0\" viewBox=\"0 0 16 16\">\n        <path d=\"M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z\"/>\n      </svg>\n    }\n    <!-- (x) -->\n    @case ('error')   {\n      <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"1em\" height=\"1em\" fill=\"currentColor\" class=\"bi bi-x-circle-fill my-1 mx-0\" viewBox=\"0 0 16 16\">\n        <path d=\"M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z\"/>\n      </svg>\n    }\n  }\n</ng-template>\n", styles: [".alert-title{font-size:1.4rem;text-transform:capitalize}.close{border-width:0}\n"], dependencies: [{ kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'uss-alert', standalone: true, imports: [
                        NgClass,
                        NgTemplateOutlet,
                    ], template: "<section #alert\n         [ngClass]=\"computedStyling()\"\n         role=\"alert\"\n         tabindex=\"-1\">\n\n  <div class=\"visually-hidden sr-only\">\n    <button (click)=\"focus()\">\n      <span>{{model().body}}</span>\n      <span>\n      @if (canFocus()) {\n        @if (model().alternateFocusDescription) {\n          {{model().alternateFocusDescription}}\n        }\n        @else {\n          Jump to\n          {{jumpToFieldName()\n          ? \"the '\" + jumpToFieldName() + \"' field\"\n          : 'the field that produced this message'}}.\n        }\n      }\n      </span>\n    </button>\n  </div>\n\n  <div class=\"col d-flex\"\n       [ngClass]=\"{'container': fullWidth()}\">\n    <!-- Icon -->\n    <div class=\"d-none d-md-flex me-3\" style=\"font-size: 4rem !important;\">\n      <ng-container [ngTemplateOutlet]=\"icon\" [ngTemplateOutletContext]=\"{type:type()}\" />\n    </div>\n\n    <!-- Content -->\n    <div class=\"d-flex flex-column justify-content-center\">\n\n      <!-- Heading -->\n      <div class=\"d-flex align-items-center mb-2\">\n\n        <!-- Mobile Icon\n             The icon shifts to here to become a part of the heading on mobile. -->\n        <div class=\"d-inline-flex d-md-none ms-0 me-2 fs-4\">\n          <ng-container [ngTemplateOutlet]=\"icon\" [ngTemplateOutletContext]=\"{type:type()}\" />\n        </div>\n\n        <h4 class=\"alert-heading fw-bold mb-0\">\n          {{model().title}}\n        </h4>\n\n      </div>\n\n      <!-- Body -->\n      <p class=\"mb-0\">{{model().body}}</p>\n\n      <!-- Focus Link -->\n      @if (canFocus()) {\n        <p class=\"mt-3 mb-0\">\n          <a href=\"\" class=\"alert-link\" (click)=\"focus()\">\n            @if (model().alternateFocusDescription) {\n              {{model().alternateFocusDescription}}\n            }\n            @else {\n              Jump to\n              {{jumpToFieldName()\n              ? 'the ' + jumpToFieldName() + ' field'\n              : 'the field that produced this message'}}.\n            }\n          </a>\n        </p>\n      }\n\n    </div>\n  </div>\n\n  <!-- Close Button -->\n  @if (closeable()) {\n    <div class=\"d-flex flex-shrink-0 justify-content-end text-end\">\n      <button type=\"button\"\n              class=\"close btn-close pt-3\"\n              (click)=\"close()\"\n              aria-label=\"Close this message\">\n      </button>\n    </div>\n  }\n\n</section>\n\n<ng-template #icon let-type=\"type\">\n  @switch (type) {\n    <!-- (i) -->\n    @case ('info')    {\n      <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"1em\" height=\"1em\" fill=\"currentColor\" class=\"bi bi-info-circle-fill my-1 mx-0\" viewBox=\"0 0 16 16\">\n        <path d=\"M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z\"/>\n      </svg>\n    }\n    <!-- (!) -->\n    @case ('warning') {\n      <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"1em\" height=\"1em\" fill=\"currentColor\" class=\"bi bi-exclamation-triangle-fill my-1 mx-0\" viewBox=\"0 0 16 16\">\n        <path d=\"M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z\"/>\n      </svg>\n    }\n    <!-- (x) -->\n    @case ('error')   {\n      <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"1em\" height=\"1em\" fill=\"currentColor\" class=\"bi bi-x-circle-fill my-1 mx-0\" viewBox=\"0 0 16 16\">\n        <path d=\"M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z\"/>\n      </svg>\n    }\n  }\n</ng-template>\n", styles: [".alert-title{font-size:1.4rem;text-transform:capitalize}.close{border-width:0}\n"] }]
        }], propDecorators: { alert: [{
                type: ViewChild,
                args: ['alert', { static: true }]
            }] } });

class AlertsComponent {
    alerts = input([]);
    remove = output();
    onClosed(notification) {
        this.remove.emit(notification);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AlertsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "18.2.1", type: AlertsComponent, isStandalone: true, selector: "uss-alerts", inputs: { alerts: { classPropertyName: "alerts", publicName: "alerts", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { remove: "remove" }, ngImport: i0, template: `
    <uss-alert *ngFor="let alert of alerts(); first as isFirst"
               [model]="alert.model"
               [type]="alert.type"
               [requestFocus]="alert.changeFocus ? isFirst : false"
               [closeable]="true"
               (closed)="onClosed(alert)"
               class="hidden-print">
    </uss-alert>
  `, isInline: true, dependencies: [{ kind: "component", type: AlertComponent, selector: "uss-alert", inputs: ["requestFocus", "model", "type", "closeable", "styling", "infoStyling", "warningStyling", "errorStyling", "fullWidth"], outputs: ["closed"] }, { kind: "directive", type: NgFor, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: AlertsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'uss-alerts',
                    standalone: true,
                    imports: [
                        AlertComponent,
                        NgFor,
                    ],
                    template: `
    <uss-alert *ngFor="let alert of alerts(); first as isFirst"
               [model]="alert.model"
               [type]="alert.type"
               [requestFocus]="alert.changeFocus ? isFirst : false"
               [closeable]="true"
               (closed)="onClosed(alert)"
               class="hidden-print">
    </uss-alert>
  `
                }]
        }] });

class NotificationService {
    router = inject(Router, { optional: true });
    _notifications = [];
    idCount = 0;
    localKeepAlive = false;
    _observable = new BehaviorSubject(this.notifications);
    constructor() {
        if (this.router) {
            this.router.events
                .pipe(takeUntilDestroyed(), filter(event => event instanceof NavigationStart))
                .subscribe(_ => {
                this.resetNotifications();
                this.keepAlive(false);
                this.notifications.forEach((notification) => notification.keepAlive = false);
            });
        }
    }
    get observable() {
        return this._observable;
    }
    addInfo(msg, changeFocus = true) {
        return this.addOfType(msg, changeFocus, 'info', 'Information');
    }
    addWarning(msg, changeFocus = true) {
        return this.addOfType(msg, changeFocus, 'warning', 'Warning');
    }
    addError(msg, changeFocus = true) {
        return this.addOfType(msg, changeFocus, 'error', 'Error');
    }
    addOfType(msg, changeFocus, type, title) {
        return this.addNotification(type, isString(msg) ? {
            title,
            body: msg
        } : msg, changeFocus);
    }
    addNotification(type, model, changeFocus) {
        const id = 'note-' + this.idCount++;
        if (changeFocus === undefined || changeFocus === null) {
            changeFocus = true;
        }
        const notification = {
            changeFocus,
            id,
            keepAlive: this.localKeepAlive,
            model: model || {},
            type: type || 'info',
        };
        if (!this.contains(notification)) {
            let notes = this.notifications.slice(0);
            notes.push(notification);
            this.setNotifications(notes);
        }
        return id;
    }
    contains(notification) {
        for (let i = 0, len = this.notifications.length; i < len; i++) {
            if (this.equals(this.notifications[i], notification)) {
                return notification;
            }
        }
        return null;
    }
    equals(one, two) {
        return one.keepAlive === two.keepAlive
            && one.type === two.type
            && one.model.body === two.model.body
            && one.model.title === two.model.title
            && one.model.focus === two.model.focus
            && one.changeFocus === two.changeFocus;
    }
    keepAlive(value) {
        this.localKeepAlive = (value === undefined || value === null) ? true : value;
        return this.localKeepAlive;
    }
    get notifications() {
        return this._notifications;
    }
    setNotifications(notes) {
        this._notifications = notes;
        this._observable.next(this._notifications);
    }
    removeNotification(id) {
        this.resetNotifications(id);
    }
    clear(id) {
        this.resetNotifications(id);
    }
    resetNotifications(id) {
        if (id) {
            this.setNotifications(this.notifications.filter((notification) => {
                return notification.id !== id;
            }));
        }
        else {
            this.setNotifications((this.notifications || []).filter((notification) => {
                return notification.keepAlive;
            }));
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: NotificationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class PageAlertComponent {
    notificationService = inject(NotificationService);
    alerts = toSignal(this.notificationService.observable, { initialValue: [] });
    hasAlerts = computed(() => this.alerts().length > 0);
    removeAlertFromAlerts(notification) {
        this.notificationService.removeNotification(notification.id);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageAlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.1", type: PageAlertComponent, isStandalone: true, selector: "uss-page-alert", ngImport: i0, template: `
    <div class="container">
        <div *ngIf="hasAlerts()" class="row page-top">
            <div class="col-lg-6 offset-lg-3 col-sm-12">
                <uss-alerts (remove)="removeAlertFromAlerts($event)" [alerts]="alerts()" style="width: 100%"></uss-alerts>
            </div>
        </div>
    </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: AlertsComponent, selector: "uss-alerts", inputs: ["alerts"], outputs: ["remove"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageAlertComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'uss-page-alert',
                    standalone: true,
                    imports: [
                        NgIf,
                        AlertsComponent,
                    ],
                    template: `
    <div class="container">
        <div *ngIf="hasAlerts()" class="row page-top">
            <div class="col-lg-6 offset-lg-3 col-sm-12">
                <uss-alerts (remove)="removeAlertFromAlerts($event)" [alerts]="alerts()" style="width: 100%"></uss-alerts>
            </div>
        </div>
    </div>
    `,
                }]
        }] });

var PageStatus;
(function (PageStatus) {
    PageStatus[PageStatus["Loading"] = 0] = "Loading";
    PageStatus[PageStatus["Loaded"] = 1] = "Loaded";
    PageStatus[PageStatus["Error"] = 2] = "Error";
})(PageStatus || (PageStatus = {}));
function emptyPage() {
    return {
        data: [],
        number: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0,
    };
}
function emptyFalliblePage(status = PageStatus.Loaded) {
    return {
        data: [],
        number: 0,
        size: 0,
        status,
        totalElements: 0,
        totalPages: 0,
    };
}
function emptyLoadingFalliblePage() {
    return {
        data: [],
        number: 0,
        size: 0,
        status: PageStatus.Loading,
        totalElements: 0,
        totalPages: 0,
    };
}
function mapPage(given, newData) {
    const newPage = {};
    Object.keys(given).forEach((key) => {
        newPage[key] = given[key];
    });
    newPage.data = newData;
    return newPage;
}
function mapFalliblePage(given, newData, status) {
    const newPage = mapPage(given, newData);
    newPage.status = status;
    return newPage;
}
function mapFalliblePageWithNewStatus(given, status) {
    const newPage = mapPage(given, given.data);
    newPage.status = status;
    return newPage;
}

class PageComponent {
    requestedPage = output();
    page = input(emptyPage());
    totalPages = computed(() => this.page().totalPages);
    totalResults = computed(() => this.page().totalElements);
    moreThanOnePage = computed(() => this.page().totalPages > 1);
    currentPage = computed(() => this.page().number);
    firstPage = computed(() => this.totalResults() > 0 && this.currentPage() === 0);
    lastPage = computed(() => this.totalResults() > 0 && this.currentPage() === this.page().totalPages - 1);
    pagesToShow = computed(() => {
        if (this.page().totalElements === 0) {
            return [];
        }
        else {
            const toDisplay = [this.currentPage()];
            const maxValues = Math.min(5, this.totalPages());
            for (let i = 1; toDisplay.length < maxValues; i++) {
                if (this.currentPage() - i >= 0) {
                    toDisplay.unshift(this.currentPage() - i);
                }
                if (this.currentPage() + i < this.totalPages()) {
                    toDisplay.push(this.currentPage() + i);
                }
            }
            return toDisplay;
        }
    });
    requestPage(pageNumber) {
        if (pageNumber !== this.currentPage()) {
            this.requestedPage.emit(pageNumber);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "18.2.1", type: PageComponent, isStandalone: true, selector: "app-page", inputs: { page: { classPropertyName: "page", publicName: "page", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { requestedPage: "requestedPage" }, ngImport: i0, template: "<div class=\"mt-5 paginator\" *ngIf=\"moreThanOnePage()\">\n  <div class=\"row\">\n    <nav class=\"col\" aria-label=\"Page navigation\">\n      <ul class=\"pagination pagination-sm justify-content-center\">\n        <li class=\"page-item\" [class.disabled]=\"firstPage()\">\n          <button type=\"button\" class=\"page-link\" (click)=\"requestPage(0)\">First</button>\n        </li>\n        <li class=\"page-item\" [class.active]=\"cur === currentPage()\"  *ngFor=\"let cur of pagesToShow()\">\n          <button type=\"button\" class=\"page-link\" (click)=\"requestPage(cur)\">{{cur + 1}}</button>\n        </li>\n        <li class=\"page-item\" [class.disabled]=\"lastPage()\">\n          <button type=\"button\" class=\"page-link\" (click)=\"requestPage(totalPages() - 1)\">Last</button>\n        </li>\n      </ul>\n    </nav>\n  </div>\n  <div class=\"row\">\n    <div class=\"col\">\n      <div class=\"total-count fw-lighter text-center\">{{totalResults()}} over {{totalPages()}} Total Pages</div>\n    </div>\n  </div>\n</div>\n", styles: [".total-count{font-size:.75em}.page-link{box-shadow:none}\n"], dependencies: [{ kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: PageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-page', standalone: true, imports: [
                        NgIf,
                        NgForOf,
                    ], template: "<div class=\"mt-5 paginator\" *ngIf=\"moreThanOnePage()\">\n  <div class=\"row\">\n    <nav class=\"col\" aria-label=\"Page navigation\">\n      <ul class=\"pagination pagination-sm justify-content-center\">\n        <li class=\"page-item\" [class.disabled]=\"firstPage()\">\n          <button type=\"button\" class=\"page-link\" (click)=\"requestPage(0)\">First</button>\n        </li>\n        <li class=\"page-item\" [class.active]=\"cur === currentPage()\"  *ngFor=\"let cur of pagesToShow()\">\n          <button type=\"button\" class=\"page-link\" (click)=\"requestPage(cur)\">{{cur + 1}}</button>\n        </li>\n        <li class=\"page-item\" [class.disabled]=\"lastPage()\">\n          <button type=\"button\" class=\"page-link\" (click)=\"requestPage(totalPages() - 1)\">Last</button>\n        </li>\n      </ul>\n    </nav>\n  </div>\n  <div class=\"row\">\n    <div class=\"col\">\n      <div class=\"total-count fw-lighter text-center\">{{totalResults()}} over {{totalPages()}} Total Pages</div>\n    </div>\n  </div>\n</div>\n", styles: [".total-count{font-size:.75em}.page-link{box-shadow:none}\n"] }]
        }] });

class SsnPipe {
    transform(ssn) {
        if (!ssn) {
            return '';
        }
        if (ssn.length !== 9) {
            return '*********';
        }
        return `*****${ssn.substring(ssn.length - 4, ssn.length)}`;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SsnPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: SsnPipe, isStandalone: true, name: "ssn" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: SsnPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ssn',
                    standalone: true,
                }]
        }] });

class TermPipe {
    SEASONS = {
        8: 'Fall',
        6: 'Summer',
        4: 'Spring',
        2: 'Winter'
    };
    transform(strm) {
        if (!strm) {
            return '';
        }
        if (strm.length !== 4) {
            return strm;
        }
        return `${this.SEASONS[strm[3]]} ${1900 + parseInt(strm.slice(0, 3), 10)}`;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TermPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: TermPipe, isStandalone: true, name: "term" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: TermPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'term',
                    standalone: true
                }]
        }] });

class UnidPipe {
    transform(emplid) {
        if (!emplid) {
            return '';
        }
        return `U${emplid.substring(1, emplid.length)}`;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UnidPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.1", ngImport: i0, type: UnidPipe, isStandalone: true, name: "unid" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: UnidPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'unid',
                    standalone: true
                }]
        }] });

class FieldError extends Error {
    field;
    constructor(msg = 'Invalid field.', elementId = '') {
        super(msg);
        this.field = elementId;
    }
}

class ValidationService {
    DEFAULT_MESSAGES = {
        date: 'Not a valid date.',
        email: 'Not a valid email address.',
        max: 'Exceeds maximum value.',
        maxlength: 'Exceeds maximum length.',
        modulus: 'Not a valid amount',
        min: 'Does not meet minimum value.',
        minlength: 'Does not meet minimum length.',
        number: 'Not a valid number.',
        parse: 'Required.',
        pattern: 'Does not match expected pattern.',
        precision: 'Not a valid amount',
        required: 'Required.',
        url: 'Not a valid URL.',
    };
    constructor() { }
    validateForm(form, msgs = {}) {
        if (!form || form.valid) {
            return new Set();
        }
        const controls = this.resolveFormControls(form);
        this.markFormDirty(controls);
        return this.generateErrorsSet(controls, msgs);
    }
    markFormDirty(controls) {
        Object.keys(controls).forEach(current => controls[current].markAsDirty());
    }
    /*
     * Look for angular-provided errors in an HTML form and contained
     * inputs. Report errors in a Set of {FieldError}s.
     */
    generateErrorsSet(controls, msgs) {
        const result = new Set();
        // split into [name, control]
        Object.entries(controls)
            // remove valid fields
            .filter(pair => pair[1].invalid)
            // map to FieldError[]
            .map(pair => this.toFieldErrorArr(pair, msgs))
            // collapse into single array
            .reduce((left, right) => left.concat(right), [])
            // add to the set
            .forEach(err => result.add(err));
        return result;
    }
    /**
     * accept a pair of [name, AbstractControl]
     *
     * map the pair to a FieldError[]
     */
    toFieldErrorArr(pair, msgs) {
        // get each error name
        return Object.keys(pair[1].errors)
            // use the error name to pull the message off of the messages and associate the error to the field(pair[0])
            .map(errorName => new FieldError(msgs[errorName] || this.DEFAULT_MESSAGES[errorName], pair[0]));
    }
    /**
     * Attempt to resolve the AbstractControls to a k,v pair on the name and the AbstractControl
     *
     * These are expected arguments and the structure we expect to get the k,v pair from:
     * [type]: [property] => [expected type]
     * NgForm: controls => {[key: string]: AbstractControl}
     * FormGroup: controls => {[key: string]: AbstractControl}
     * NgControl: control => AbstractControl (also contains the name property)
     * NgModel: this extends NgControl and will be treated the same as above
     * FormControl: self => AbstractControl (get the parent to resolve the name)
     */
    resolveFormControls(form) {
        // return if undefined or a FormArray or the form is considered valid
        if (!form || form.length || form.valid) {
            return {};
        }
        // FormGroup or NgForm have the k,v map we expect
        if (form.controls) {
            return form.controls;
        }
        // This resolves NgControl and it's child type NgModel
        const ngControlForm = form;
        if (ngControlForm.control) {
            const result = {};
            result[ngControlForm.name || ''] = ngControlForm.control;
            return result;
        }
        // This resolves FormControl. Use the parent to get the k,v pair from FormGroup
        const control = form;
        return this.resolveFormControls(control.parent);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ValidationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ValidationService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.1", ngImport: i0, type: ValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

/*
 * Public API Surface of angular-util

 */

/**
 * Generated bundle index. Do not edit.
 */

export { AbbreviatePipe, AccessibilityDirective, AlertComponent, AlertsComponent, AmountInputDirective, AmountInputValidator, BytePipe, FieldError, NotificationService, PageAlertComponent, PageComponent, PageStatus, SsnPipe, TermPipe, UnidPipe, ValidationService, emptyFalliblePage, emptyLoadingFalliblePage, emptyPage, isFunction, isString, mapFalliblePage, mapFalliblePageWithNewStatus, mapPage };
